import { useEffect, useState } from 'react';
import { Trash2, ShieldCheck } from 'lucide-react';
import TopNav from '@/components/TopNav';
import { AuthorSignature } from '@/components/AuthorSignature';
import { Toaster, toast } from '@/components/ui/sonner';
import { activeSounds } from '@/lib/activeSounds';

const UPDATED = '1 giugno 2026';
const SITE = 'https://www.mauriziopinkrosa.it';

const Section = ({ id, title, children }) => (
  <section id={id} data-testid={`info-section-${id}`} className="bg-[#111112] border border-white/5 rounded-2xl p-5 sm:p-7 flex flex-col gap-3">
    <h2 className="font-bebas text-2xl sm:text-3xl tracking-wide leading-none text-white">{title}</h2>
    <div className="text-sm leading-relaxed text-white/70 flex flex-col gap-3 [&_strong]:text-white [&_a]:text-pink-300 [&_a]:underline [&_li]:ml-4 [&_li]:list-disc">{children}</div>
  </section>
);

const ClearData = () => {
  const [confirm, setConfirm] = useState(false);
  const clear = () => {
    activeSounds.stopAll();
    localStorage.clear();
    toast.success('Dati cancellati: l\'app riparte con le impostazioni iniziali');
    setConfirm(false);
    setTimeout(() => window.location.reload(), 900);
  };
  return confirm ? (
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-pink-300">Cancellare preset, sequenze, statistiche e impostazioni da questo dispositivo?</span>
      <button data-testid="info-clear-confirm" onClick={clear} className="h-10 px-4 rounded-full bg-rose-500 text-white text-xs font-semibold uppercase tracking-[0.2em]">Sì, cancella</button>
      <button data-testid="info-clear-cancel" onClick={() => setConfirm(false)} className="h-10 px-4 rounded-full border border-white/15 text-xs uppercase tracking-[0.2em] text-white/70">Annulla</button>
    </div>
  ) : (
    <button data-testid="info-clear-data" onClick={() => setConfirm(true)} className="self-start h-10 px-4 rounded-full border border-rose-400/50 text-rose-300 text-xs uppercase tracking-[0.2em] inline-flex items-center gap-2 hover:bg-rose-400/10">
      <Trash2 className="w-4 h-4" /> Cancella tutti i miei dati
    </button>
  );
};

export default function InfoPage() {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  return (
    <div className="relative min-h-screen text-white pb-8" data-testid="info-root">
      <div className="bg-foam" />
      <div className="bg-grain" />
      <div className="relative z-10 max-w-3xl mx-auto px-3 sm:px-4 md:px-8 py-3 md:py-6 flex flex-col gap-4 sm:gap-6">
        <TopNav />
        <header className="px-1">
          <div className="text-[10px] uppercase tracking-[0.4em] text-white/40">Informazioni · privacy · note legali</div>
          <h1 className="font-bebas text-4xl sm:text-5xl lg:text-6xl leading-none mt-1">Info<span className="text-pink-400">.</span></h1>
          <p className="text-xs text-white/40 mt-2">Ultimo aggiornamento: {UPDATED}</p>
        </header>

        <Section id="app" title="L'app">
          <p>Metronomo, sequencer, accordatore cromatico e bordone per musicisti, gratuiti e senza pubblicità. Si può installare sul telefono come app (PWA) e funziona anche offline dopo la prima apertura.</p>
          <ul>
            <li><strong>Player</strong>: metronomo con suddivisioni, accenti, conto iniziale, accelerazione, modalità brano e kit drum.</li>
            <li><strong>Sequencer</strong>: battute personalizzabili fino a 100, ogni quarto attivabile o disattivabile.</li>
            <li><strong>Tuner</strong>: accordatore cromatico con trasposizione, accordature alternative e suoni di riferimento.</li>
            <li><strong>Bordone</strong>: nota o accordi di riferimento a tempo, scala ed esercizi sugli intervalli.</li>
          </ul>
          <p>I suoni possono suonare insieme e continuano anche a schermo bloccato finché non li spegni dalla barra in basso o dalla schermata di blocco.</p>
        </Section>

        <Section id="owner" title="Titolare del trattamento">
          <p><strong>Maurizio "Pink" Rosa</strong> — per qualsiasi richiesta sulla privacy usa i contatti indicati sul sito <a href={SITE} target="_blank" rel="noopener noreferrer">mauriziopinkrosa.it</a>.</p>
        </Section>

        <Section id="privacy" title="Privacy in breve">
          <p className="flex items-start gap-2"><ShieldCheck className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" /> Non ci sono account, registrazioni o pagamenti nell'app. Non raccogliamo il tuo nome, la tua email o altri dati personali inseriti da te.</p>
          <p>Questa informativa è resa ai sensi degli artt. 13 e 14 del Regolamento (UE) 2016/679 (GDPR).</p>
        </Section>

        <Section id="local" title="Dati salvati sul tuo dispositivo">
          <p>Impostazioni, preset, sequenze di accordi salvate, scale personali e statistiche degli esercizi vengono salvati solo nella memoria del tuo browser (localStorage). Non vengono inviati a noi né a terzi e puoi cancellarli in qualsiasi momento:</p>
          <ClearData />
        </Section>

        <Section id="mic" title="Microfono e MIDI">
          <p>Il <strong>microfono</strong> si attiva solo con il tuo permesso, quando usi l'accordatore, la scala o l'esercizio intervalli. Il suono viene analizzato in tempo reale sul dispositivo solo per riconoscere l'altezza della nota: <strong>non viene registrato, salvato né inviato</strong> da nessuna parte. Puoi revocare il permesso dalle impostazioni del browser.</p>
          <p>Le tastiere e i controller <strong>MIDI</strong> collegati vengono letti solo localmente (BPM, Start/Stop, note del bordone).</p>
        </Section>

        <Section id="share" title="Link e QR condivisi">
          <p>Accordature, kit, esercizi e sequenze condivisi sono contenuti per intero nel link stesso: non vengono caricati su alcun server. Chi riceve il link vede solo le impostazioni musicali che hai scelto di condividere. Prima di applicarle all'app ti viene sempre chiesto di confermare.</p>
        </Section>

        <Section id="stats" title="Statistiche di utilizzo">
          <p>All'apertura e poi ogni 5 minuti di utilizzo, l'app invia una semplice richiesta al sito <a href={SITE} target="_blank" rel="noopener noreferrer">mauriziopinkrosa.it</a> con parametri che indicano che la visita proviene da questa app (<code className="font-mono text-xs">utm_source=pwa_metronomo</code>). Come per ogni richiesta web, il server del sito riceve dati tecnici quali indirizzo IP, tipo di browser e data/ora. Serve solo a sapere quanto viene usata l'app; non vengono usati cookie dall'app né vengono creati profili.</p>
          <p><strong>Base giuridica</strong>: legittimo interesse del titolare a misurare in forma aggregata l'utilizzo di uno strumento gratuito (art. 6.1.f GDPR). I dati tecnici sono trattati secondo l'informativa del sito mauriziopinkrosa.it.</p>
        </Section>

        <Section id="third" title="Servizi di terze parti">
          <ul>
            <li><strong>Google Fonts</strong> (Google LLC): i caratteri grafici vengono scaricati dai server Google, che ricevono indirizzo IP e dati tecnici del browser.</li>
            <li><strong>Piattaforma di sviluppo e hosting (Emergent)</strong>: la pagina include strumenti di analisi tecnica della piattaforma (PostHog) che possono raccogliere dati di navigazione anonimi o pseudonimi, come pagine visitate e interazioni, per il funzionamento e il miglioramento del servizio.</li>
            <li><strong>Buy Me a Coffee</strong> e il sito dell'autore: si aprono solo se tocchi i relativi pulsanti, e valgono le loro informative.</li>
          </ul>
          <p>Alcuni di questi fornitori possono trattare dati fuori dall'Unione Europea, sulla base delle garanzie previste dal GDPR (es. EU-US Data Privacy Framework o clausole contrattuali standard).</p>
        </Section>

        <Section id="rights" title="I tuoi diritti">
          <p>Puoi chiedere in ogni momento accesso, rettifica, cancellazione, limitazione od opposizione al trattamento dei tuoi dati (artt. 15–22 GDPR), contattando il titolare tramite il sito. Hai anche il diritto di proporre reclamo al <a href="https://www.garanteprivacy.it" target="_blank" rel="noopener noreferrer">Garante per la protezione dei dati personali</a>.</p>
          <p>I dati tecnici delle richieste web sono conservati per il tempo strettamente necessario secondo le politiche dei rispettivi server. I dati sul dispositivo restano finché non li cancelli.</p>
        </Section>

        <Section id="legal" title="Note legali">
          <p>L'app è fornita gratuitamente "così com'è", senza garanzie di precisione assoluta del tempo o dell'intonazione. Proteggi l'udito: usa volumi moderati, soprattutto in cuffia. L'informativa può essere aggiornata: la data in alto indica l'ultima modifica.</p>
          <p>© {new Date().getFullYear()} Maurizio "Pink" Rosa. Tutti i diritti riservati.</p>
        </Section>

        <footer className="mt-6 mb-4"><AuthorSignature /></footer>
      </div>
      <Toaster position="bottom-right" />
    </div>
  );
}
